/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import { addDays, format } from 'date-fns';
import type { Column, Columns } from '../../../main/types/tableTypes';
import type { ReportData } from '../../../main/types/analytics';
import { DRUID_TO_HUMAN_READABLE } from '../../../common/support/eo_mapping';
type ColumnHierarchy = {
  headerColumns: Columns;
  subColumns: Columns;
};
export function getDefaultSelectedColumns(columns: any, selectedKeys: Array<string>) {
  return selectedKeys
    .map((colKey) => {
      return columns.find(R.propEq('key', colKey));
    })
    .filter((column) => !!column);
}
export function getRowValuesFromColumnHelpers(
  columns: Columns = [],
  data: Array<Record<string, any>> = []
): Array<Record<string, any>> {
  return data.map((row) => {
    return columns.reduce((acc, col) => {
      const val = col.getValue ? col.getValue(row) : row[col.key];
      return R.assoc(col.key, val, acc);
    }, row);
  });
}
export function cleanReportValues(columns: Columns = [], data: ReportData = []): ReportData | undefined {
  if (!data) {
    return data;
  }

  return data
    .filter((row) => !!row)
    .map((row) =>
      columns.reduce((acc, col) => {
        const key = col.reportDataKey;
        const rawVal = row[key];
        acc[key] = col.cleanValue ? col.cleanValue(rawVal) : rawVal;
        return acc;
      }, row)
    );
}

const _toUTCDate = (date) => new Date(date.replace(/\-/g, '/'));

const _getGranularityFormatter = (granularity) => {
  switch (granularity) {
    case 'month':
      return (date) => format(_toUTCDate(date), 'yyyy/MM');

    case 'week':
      return (date) => {
        const startDate = _toUTCDate(date);

        const endDate = addDays(startDate, 6);
        const weekStart = format(startDate, 'yyyy/MM/dd');
        const weekEnd = format(endDate, 'yyyy/MM/dd');
        return `${weekStart} - ${weekEnd}`;
      };

    case 'day':
      return (date) => format(_toUTCDate(date), 'yyyy/MM/dd');

    default:
      return (date) => date;
  }
};

export function dateRangeFormatter(granularity) {
  if (!['month', 'week', 'day'].includes(granularity)) {
    return (col) => col;
  }

  const formatter = _getGranularityFormatter(granularity);

  return (col) => {
    if (!col || col.key !== 'date_range') {
      return col;
    }

    col.formatter = (date) => (date ? formatter(date) : '');

    return col;
  };
}
export const addHeaderColumn = (hierarchy: ColumnHierarchy, column: Column, key: string): ColumnHierarchy => {
  return R.assoc(
    'headerColumns',
    hierarchy.headerColumns.concat({
      key,
      display: DRUID_TO_HUMAN_READABLE[key] || key,
      subCols: [column],
      no_sort: true,
      type: column.type,
    }),
    hierarchy
  );
};
export const updateHeaderColumn = (hierarchy: ColumnHierarchy, column: Column, key: string): ColumnHierarchy => {
  const colHeaderInd = R.findIndex(R.propEq('key', key), hierarchy.headerColumns);
  const subColLens = R.lensPath(['headerColumns', colHeaderInd, 'subCols']);
  return R.set(subColLens, R.view(subColLens, hierarchy).concat(column), hierarchy);
};
export const addSubColumn = (hierarchy: ColumnHierarchy, column: Column): ColumnHierarchy => {
  return R.assoc('subColumns', hierarchy.subColumns.concat(column), hierarchy);
};
export const generateColumnHierarchy = (columns: Columns): ColumnHierarchy => {
  return columns.reduce(
    (acc, column) => {
      // case: incoming column is a subcolumn
      if (R.has('group', column)) {
        const key = R.path(['group', 'key'], column);

        // create the header column and add the current column to it's subCols prop
        if (!R.find(R.propEq('key', key), acc.headerColumns)) {
          acc = addHeaderColumn(acc, column, key);
          return addSubColumn(acc, column);
        }

        // add column to acc.headerColumn.subCols
        acc = updateHeaderColumn(acc, column, key);
        // add column to acc.subColumns in either case
        return addSubColumn(acc, column);
      }

      return R.assoc('headerColumns', acc.headerColumns.concat(column), acc);
    },
    {
      headerColumns: [],
      subColumns: [],
    }
  );
};
export const getDataSourcesFromColumns = (selectedColumns: Columns) => {
  return [
    ...new Set(
      selectedColumns && // $FlowFixMe
        selectedColumns.flatMap((column) => {
          return column.dataSources;
        })
    ),
  ].filter((column) => !!column);
};
// ensures backwards compatibility of bookmarks for those created before the dynamic data loading table model & domain analytics
export const regenerateSelectedColumns = (selectedColumns: Columns, allColumns: Columns) => {
  if (!selectedColumns) {
    return selectedColumns;
  }

  const missingDomainAnalyticsKeys = selectedColumns.some((c) => !c.reportDataKey);

  if (missingDomainAnalyticsKeys) {
    return selectedColumns.map((column) => {
      return allColumns.find((c) => c.key === column.key) || column;
    });
  }

  const dataSources = getDataSourcesFromColumns(selectedColumns);

  if (!dataSources.length) {
    return selectedColumns.map((column) => {
      return allColumns.find((c) => c.key === column.key) || column;
    });
  }

  return selectedColumns;
};
